import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useModel } from '@umijs/max';

const { Text } = Typography;

// 时区配置类型
interface TimezoneConfig {
  timezone: string;
  flag: string;
  label?: string;
}

// 时钟组件配置类型
interface ClockConfig {
  timezones: TimezoneConfig[];
  format?: string;
  style?: React.CSSProperties;
}

// 高性能多时区时钟组件
const Clock: React.FC<ClockConfig> = React.memo(({
  timezones = [
    { timezone: 'Asia/Tokyo', flag: '🇯🇵', label: '日本' }
  ],
  format = 'YYYY-MM-DD HH:mm:ss',
  style
}) => {
  const [time, setTime] = useState(() => dayjs());
  const { timezone: currentTimezone, setTimezone } = useModel('global');
  
  // 使用 ref 来避免闭包问题
  const lastUpdateTimeRef = useRef(0);
  const animationIdRef = useRef<number | undefined>(undefined);

  // 优化的时间更新逻辑
  useEffect(() => {
    const UPDATE_INTERVAL = 1000; // 1秒更新一次

    const updateTime = (currentTime: number) => {
      if (currentTime - lastUpdateTimeRef.current >= UPDATE_INTERVAL) {
        setTime(dayjs());
        lastUpdateTimeRef.current = currentTime;
      }
      animationIdRef.current = requestAnimationFrame(updateTime);
    };

    animationIdRef.current = requestAnimationFrame(updateTime);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  // 缓存时区选项的计算
  const options = useMemo(() => {
    return timezones.map(config => {
      const zoneTime = time.tz(config.timezone);
      return {
        value: config.timezone,
        label: (
          <Space size="small">
            <Text>{config.flag}</Text>
            <Text>{config.label || config.timezone.split('/')[1]}</Text>
            <Text
              style={{
                fontSize: '12px',
                fontFamily: 'SF Mono, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
                letterSpacing: '0.3px'
              }}
            >
              {zoneTime.format(format)}
            </Text>
          </Space>
        )
      };
    });
  }, [timezones, time, format]);

  // 缓存 onChange 回调
  const handleTimezoneChange = useCallback((value: string) => {
    setTimezone(value);
  }, [setTimezone]);

  return (
    <Select
      value={currentTimezone}
      onChange={handleTimezoneChange}
      size="small"
      style={style}
      options={options}
    />
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数，只在必要时重新渲染
  return (
    prevProps.timezones === nextProps.timezones &&
    prevProps.format === nextProps.format &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
});

// 设置显示名称，便于调试
Clock.displayName = 'Clock';

export default Clock;
